// Leveraged LP Farming — dedicated page (kept fully separate from the homepage).
// Every user gets an isolated position: their own position contract, deployed by the
// market's factory, holding their own Blend position. A liquidation only touches that user.
//
// See docs/leverage/ and soroban-contracts/leverage-vault (factory, position, zapper).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import type { RootState } from "../../lib/store";
import { SOROBAN_CONFIG } from "../../config/soroban.config";
import {
  sorobanLeverageService,
  type LeverageHealth,
  type LeverageMarketConfig,
} from "../../services/soroban-leverage.service";
import aquaLogo from "../../assets/images/aqua_logo.png";
import usdcLogo from "../../assets/images/usdc.svg";
import xlmLogo from "../../assets/images/xlm.png";

const short = (a?: string | null) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "—");

/** A typed amount the contracts accept: finite, ≥ 0, at most 7 decimals. Empty = 0. */
const amountOk = (v: string) => v.trim() === "" || /^\d*\.?\d{0,7}$/.test(v.trim());
const amount = (v: string) => (v.trim() === "" ? 0 : parseFloat(v));

const TOKEN_LOGOS: Record<string, string> = {
  AQUA: aquaLogo,
  USDC: usdcLogo,
  XLM: xlmLogo,
  BLUB: "/blub_logo.png",
};

const markets = SOROBAN_CONFIG.leverage.markets;
type Market = (typeof markets)[number];

function TokenIcon({ code, size = 20 }: { code: string; size?: number }) {
  const src = TOKEN_LOGOS[code];
  if (!src) {
    return (
      <span
        className="inline-flex items-center justify-center rounded-full bg-white/10 text-[9px] text-white/70"
        style={{ width: size, height: size }}
      >
        {code.slice(0, 3)}
      </span>
    );
  }
  return (
    <img src={src} alt={code} className="rounded-full" style={{ width: size, height: size }} />
  );
}

/** Overlapping pair icons (token A over token B). */
function PairIcons({ a, b, size = 22 }: { a: string; b: string; size?: number }) {
  return (
    <span className="inline-flex items-center" style={{ width: size * 1.6 }}>
      <TokenIcon code={a} size={size} />
      <span style={{ marginLeft: -size / 3 }}>
        <TokenIcon code={b} size={size} />
      </span>
    </span>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-white/60">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  suffix,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  suffix?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-white/50">{label}</span>
      <div className="flex items-center rounded-xl border border-white/10 bg-black/30 px-3">
        <input
          // type="text" + inputMode avoids browser-locale comma formatting (see frontend-notes memory)
          type="text"
          inputMode="decimal"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value.replace(/,/g, "."))}
          className="w-full bg-transparent py-3 text-white outline-none placeholder:text-white/25"
        />
        {suffix && <span className="pl-2 text-xs text-white/40">{suffix}</span>}
      </div>
    </label>
  );
}

export default function Leverage() {
  const user = useSelector((state: RootState) => state.user);
  const configured = sorobanLeverageService.isConfigured();
  const wallet = user?.userWalletAddress;
  const walletName = user?.walletName || "freighter";

  const [selectedId, setSelectedId] = useState<string>(markets[0]?.id ?? "");
  const market: Market | undefined = useMemo(
    () => markets.find((m) => m.id === selectedId) ?? markets[0],
    [selectedId]
  );

  const [cfg, setCfg] = useState<LeverageMarketConfig | null>(null);
  const [factors, setFactors] = useState<{ cFactor: number; lFactor: number } | null>(null);
  const [positionCount, setPositionCount] = useState(0);
  const [positionAddr, setPositionAddr] = useState<string | null>(null);
  const [health, setHealth] = useState<LeverageHealth | null>(null);
  // null = could not be read; the toggle is disabled rather than guessed
  const [managerEnabled, setManagerEnabled] = useState<boolean | null>(null);
  // undefined = position lookup failed (RPC); null = no position yet
  const [positionLookup, setPositionLookup] = useState<"ok" | "failed">("ok");
  const [lpValue, setLpValue] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Open form
  const [collateralLp, setCollateralLp] = useState("");
  const [borrowAmount, setBorrowAmount] = useState("");
  const [slippagePct, setSlippagePct] = useState("1.0");
  // Unwind forms
  const [repayAmount, setRepayAmount] = useState("");
  const [withdrawLp, setWithdrawLp] = useState("");
  const [delevLp, setDelevLp] = useState("");

  // Each refresh gets an id; a slower, older refresh never overwrites a newer one.
  const refreshId = useRef(0);
  const refresh = useCallback(async () => {
    if (!configured || !market?.factory) return;
    const id = ++refreshId.current;
    const factory = market.factory;
    const current = () => id === refreshId.current;
    setLoading(true);
    try {
      sorobanLeverageService.setMarket(factory);
      const [c, n, f, v] = await Promise.all([
        sorobanLeverageService.getMarketConfig(factory),
        sorobanLeverageService.getPositionCount(factory),
        sorobanLeverageService.getReserveFactors(market.blendPool, market.lpToken, market.borrowAsset),
        sorobanLeverageService.getLpValueInBorrow(market.amm),
      ]);
      if (!current()) return;
      setCfg(c);
      setPositionCount(n);
      setFactors(f);
      setLpValue(v);
      if (wallet) {
        const addr = await sorobanLeverageService.getPositionAddress(wallet, factory);
        if (!current()) return;
        setPositionLookup(addr === undefined ? "failed" : "ok");
        setPositionAddr(addr ?? null);
        if (addr) {
          const [h, me] = await Promise.all([
            sorobanLeverageService.getHealth(addr),
            sorobanLeverageService.isManagerEnabled(addr),
          ]);
          if (!current()) return;
          setHealth(h);
          setManagerEnabled(me);
        } else {
          setHealth(null);
          setManagerEnabled(null);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (current()) setLoading(false);
    }
  }, [configured, market?.factory, market?.blendPool, market?.lpToken, market?.borrowAsset, market?.amm, wallet]);

  // Reset inputs + reload whenever the market or wallet changes.
  useEffect(() => {
    setCfg(null);
    setPositionAddr(null);
    setHealth(null);
    setCollateralLp("");
    setBorrowAmount("");
    setRepayAmount("");
    setWithdrawLp("");
    setDelevLp("");
    refresh();
  }, [refresh]);

  // Live zap quote, read from the AMM's actual reserves (mirrors the on-chain math).
  const [quote, setQuote] = useState<{ pairOut: number; lpOut: number } | null>(null);
  const [quoting, setQuoting] = useState(false);
  useEffect(() => {
    const borrow = parseFloat(borrowAmount || "0");
    if (!market?.amm || !(borrow > 0)) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setQuoting(true);
    const t = setTimeout(async () => {
      const q = await sorobanLeverageService.getZapQuote(market.amm, borrowAmount);
      if (!cancelled) {
        setQuote(q);
        setQuoting(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
      setQuoting(false);
    };
  }, [borrowAmount, market?.amm]);

  // Live unzap quote for a deleverage, tagged with the amount it was computed for so a stale
  // quote can never set the slippage floor of a different amount.
  const [unzapQuote, setUnzapQuote] = useState<{ lp: string; out: number } | null>(null);
  const unzap = unzapQuote && unzapQuote.lp === delevLp ? unzapQuote.out : null;
  useEffect(() => {
    setUnzapQuote(null);
    const lp = parseFloat(delevLp || "0");
    if (!market?.amm || !(lp > 0) || !amountOk(delevLp)) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      const q = await sorobanLeverageService.getUnzapQuote(market.amm, delevLp);
      if (!cancelled && q != null) setUnzapQuote({ lp: delevLp, out: q });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [delevLp, market?.amm]);

  // Slippage clamped to 0–50%; anything unparseable falls back to 1%.
  const slipPct = parseFloat(slippagePct);
  const slip = (Number.isFinite(slipPct) ? Math.min(50, Math.max(0, slipPct)) : 1) / 100;
  const inputsOk = amountOk(collateralLp) && amountOk(borrowAmount);
  const capX = cfg ? cfg.maxLeverageBps / 10_000 : null;
  const minHf = cfg ? cfg.minOpenHfBps / 10_000 : null;
  const triggerHf = cfg ? cfg.deleverageHfBps / 10_000 : null;

  // LP price in borrow units: the oracle's, as the position values it, when there is a position;
  // otherwise the AMM's. Only used for the preview — the contract re-checks on-chain.
  const lpPrice = useMemo(() => {
    if (health && health.collateralLp > 0 && health.debt > 0) {
      return (health.collateralValue / health.collateralLp) * (health.debt / health.debtValue);
    }
    return lpValue;
  }, [health, lpValue]);

  // The resulting position after this open: existing + your LP + the zapped LP, against debt.
  const preview = useMemo(() => {
    const coll = parseFloat(collateralLp || "0");
    const borrow = parseFloat(borrowAmount || "0");
    if (!quote || !(borrow > 0) || !lpPrice || !inputsOk) return null;
    // The contract supplies exactly collateral + min_lp_out; LP minted above that is returned
    // to the wallet. So the preview uses the same min_lp_out the transaction will send.
    const suppliedFromZap = Math.max(0, quote.lpOut * (1 - slip));
    const totalLp = (health?.collateralLp ?? 0) + coll + suppliedFromZap;
    const totalDebt = (health?.debt ?? 0) + borrow;
    const cv = totalLp * lpPrice;
    const leverage = cv > totalDebt ? cv / (cv - totalDebt) : Infinity;
    const hf = factors ? (cv * factors.cFactor) / (totalDebt / factors.lFactor) : null;
    return {
      lpFromZap: quote.lpOut,
      pairFromSwap: quote.pairOut,
      addedCollateral: coll + suppliedFromZap,
      returnedLp: quote.lpOut - suppliedFromZap,
      borrow,
      totalLp,
      totalDebt,
      leverage,
      hf,
      overCap: capX != null && leverage > capX,
      underHf: hf != null && minHf != null && hf < minHf,
    };
  }, [collateralLp, borrowAmount, quote, lpPrice, health, factors, capX, minHf, slip, inputsOk]);

  const run = async (label: string, fn: () => Promise<{ success: boolean; error?: string }>, onOk?: () => void) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res.success) {
        toast.success(label);
        onOk?.();
        await refresh();
      } else {
        toast.error(res.error || "Transaction failed.");
      }
    } catch (e: any) {
      toast.error(e?.message || "Transaction failed.");
    } finally {
      setBusy(false);
    }
  };

  const ensurePosition = async (): Promise<string | null> => {
    if (positionAddr) return positionAddr;
    if (!wallet) return null;
    toast.info("First, sign once to create your own position contract.");
    const res = await sorobanLeverageService.createPosition(wallet, walletName);
    if (!res.success) {
      toast.error(res.error || "Could not create your position.");
      return null;
    }
    // The new contract can take a moment to be readable; retry before giving up.
    for (let i = 0; i < 5; i++) {
      const addr = await sorobanLeverageService.getPositionAddress(wallet, market?.factory);
      if (addr) {
        setPositionAddr(addr);
        return addr;
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    toast.error("Your position was created but could not be read yet. Refresh and open again.");
    return null;
  };

  const onOpen = async () => {
    if (!wallet) return toast.warn("Please connect wallet.");
    if (!market) return;
    if (cfg?.paused) return toast.info("This market is paused for new positions.");
    if (!inputsOk) return toast.warn("Amounts must be numbers with at most 7 decimals.");
    if (!(amount(borrowAmount) > 0)) return toast.warn("Enter a borrow amount.");
    if (!quote) return toast.warn("Still quoting the zap against live pool reserves — try again in a moment.");
    if (preview?.overCap) return toast.error(`That's ${preview.leverage.toFixed(2)}× — above the ${capX?.toFixed(2)}× cap.`);
    if (preview?.underHf) return toast.error(`Health factor would be ${preview.hf?.toFixed(2)} — below the ${minHf?.toFixed(2)} minimum.`);
    setBusy(true);
    try {
      const addr = await ensurePosition();
      if (!addr) return;
      const res = await sorobanLeverageService.openPosition({
        positionAddress: addr,
        userAddress: wallet,
        walletName,
        collateralLpAmount: collateralLp || "0",
        borrowAmount,
        minLpOut: Math.max(0, quote.lpOut * (1 - slip)).toFixed(7),
        minPairOut: Math.max(0, quote.pairOut * (1 - slip)).toFixed(7),
        errorCtx: { zapper: market.zapper, amm: market.amm, blendPool: market.blendPool },
      });
      if (res.success) {
        toast.success("Leveraged position opened.");
        setCollateralLp("");
        setBorrowAmount("");
        await refresh();
      } else {
        toast.error(res.error || "Open failed.");
      }
    } catch (e: any) {
      toast.error(e?.message || "Open failed.");
    } finally {
      setBusy(false);
    }
  };

  const onRepayWithdraw = () => {
    if (!wallet || !positionAddr) return toast.warn("No position to unwind.");
    if (!amountOk(repayAmount) || !amountOk(withdrawLp)) {
      return toast.warn("Amounts must be numbers with at most 7 decimals.");
    }
    // Only skip the repay when the debt is KNOWN to be zero (the contract skips it too).
    const noDebt = health?.debt === 0;
    const repay = noDebt ? 0 : amount(repayAmount);
    if (noDebt && amount(repayAmount) > 0 && !(amount(withdrawLp) > 0)) {
      return toast.info("This position has no debt to repay.");
    }
    if (!(repay > 0) && !(amount(withdrawLp) > 0)) {
      return toast.warn("Enter a repay and/or withdraw amount.");
    }
    return run(
      "Position updated.",
      () =>
        sorobanLeverageService.repayAndWithdraw({
          positionAddress: positionAddr,
          userAddress: wallet,
          walletName,
          repayAmount: String(repay),
          withdrawLpAmount: withdrawLp || "0",
        }),
      () => {
        setRepayAmount("");
        setWithdrawLp("");
      }
    );
  };

  const onDeleverage = () => {
    if (!wallet || !positionAddr) return toast.warn("No position to deleverage.");
    if (!amountOk(delevLp) || !(amount(delevLp) > 0)) return toast.warn("Enter an LP amount.");
    if (unzap == null) return toast.warn("Still quoting against live pool reserves — try again in a moment.");
    return run(
      "Deleveraged.",
      () =>
        sorobanLeverageService.deleverage({
          positionAddress: positionAddr,
          userAddress: wallet,
          walletName,
          lpAmount: delevLp,
          minBorrowOut: Math.max(0, unzap * (1 - slip)).toFixed(7),
          errorCtx: { zapper: market?.zapper, amm: market?.amm, blendPool: market?.blendPool },
        }),
      () => setDelevLp("")
    );
  };

  const onToggleManager = () => {
    if (!wallet || !positionAddr || managerEnabled == null) return;
    return run(managerEnabled ? "Keeper access revoked." : "Keeper access restored.", () =>
      sorobanLeverageService.setManagerEnabled({
        positionAddress: positionAddr,
        userAddress: wallet,
        walletName,
        enabled: !managerEnabled,
      })
    );
  };

  const hfTone = (hf: number | null | undefined) =>
    hf == null
      ? "text-white/90"
      : triggerHf != null && hf < triggerHf
      ? "text-red-300"
      : minHf != null && hf < minHf
      ? "text-amber-300"
      : "text-[#7fdca9]";

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 text-white">
      <header className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Leveraged LP Farming</h1>
          <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-medium text-amber-300">
            Testnet
          </span>
        </div>
        <p className="mt-2 max-w-2xl text-sm text-white/50">
          Use your LP position as collateral on Blend, flash-borrow against it, and recycle it
          into more LP — in one transaction. Your position is your own contract: no other user's
          liquidation can touch it. Leverage amplifies both yield and liquidation risk.
        </p>
      </header>

      {/* ── Market selector ─────────────────────────────────────────── */}
      {configured && (
        <section className="mb-8">
          <p className="mb-2 text-xs uppercase tracking-wide text-white/40">Market</p>
          <div className="flex flex-wrap gap-3">
            {markets.map((m) => {
              const active = m.id === selectedId;
              return (
                <button
                  key={m.id}
                  onClick={() => setSelectedId(m.id)}
                  className={`flex items-center gap-3 rounded-2xl border px-4 py-3 transition ${
                    active
                      ? "border-[#37b06f]/60 bg-[#37b06f]/10"
                      : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"
                  }`}
                >
                  <PairIcons a={m.tokenA} b={m.tokenB} />
                  <span className="text-left">
                    <span className="block text-sm font-semibold text-white">{m.label}</span>
                    <span className="block text-[11px] text-white/45">
                      Blend {m.blendVersion} · borrow {m.borrowSymbol}
                    </span>
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      m.status === "live" ? "bg-[#37b06f]/15 text-[#37b06f]" : "bg-amber-500/15 text-amber-300"
                    }`}
                  >
                    {m.status === "live" ? "Live" : "Activating"}
                  </span>
                </button>
              );
            })}
          </div>
          {market?.note && <p className="mt-2 text-[11px] text-white/40">{market.note}</p>}
        </section>
      )}

      {/* ── Explainers ───────────────────────────────────────────────── */}
      <section className="mb-8 grid grid-cols-1 gap-5 md:grid-cols-3">
        <Card title="How it works">
          <ol className="space-y-2 text-sm text-white/70">
            <li><span className="text-white/40">1.</span> You supply an LP token as collateral (your equity).</li>
            <li><span className="text-white/40">2.</span> Your position <span className="text-white/90">flash-borrows</span> against it.</li>
            <li><span className="text-white/40">3.</span> It <span className="text-white/90">zaps</span> the borrow into more LP (swap half → add liquidity).</li>
            <li><span className="text-white/40">4.</span> All the LP becomes collateral; the borrow stays as debt.</li>
            <li><span className="text-white/40">5.</span> The contract checks the result: leverage cap and minimum health factor.</li>
          </ol>
        </Card>

        <Card title="Where the yield comes from">
          <p className="text-sm text-white/70">
            Leverage doesn't create yield — it <span className="text-white/90">multiplies</span> the
            LP's own yield: trading fees and liquidity incentives.
          </p>
          <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3 font-mono text-[11px] text-emerald-300/90">
            Net APY = L · LP_APY − (L−1) · Borrow_APR
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-white/35">
            Example: at 1.4× with a 13% LP yield and an 8% borrow rate, 1.4·13 − 0.4·8 ≈ 15%.
            Profitable only while the LP yield beats the borrow rate. Rewards that must be claimed
            by the LP holder accrue to the lending pool while your LP is collateral; fees built
            into the LP's value compound for you.
          </p>
        </Card>

        <Card title="How you're protected">
          <ul className="space-y-2 text-sm text-white/70">
            <li>
              <span className="text-[#7fdca9]">Isolated</span> — your own contract and Blend position;
              liquidations never spread between users.
            </li>
            <li>
              <span className="text-[#7fdca9]">Health floor</span> — opens below health factor{" "}
              {minHf?.toFixed(2) ?? "—"} revert on-chain.
            </li>
            <li>
              <span className="text-[#7fdca9]">Keeper</span> — may only deleverage below{" "}
              {triggerHf?.toFixed(2) ?? "—"}, within 2% of the oracle price. It can't withdraw or
              borrow, and you can revoke it.
            </li>
          </ul>
        </Card>
      </section>

      <div className="mb-8 rounded-xl border border-sky-500/20 bg-sky-500/[0.06] p-4 text-xs leading-relaxed text-sky-200/80">
        <span className="font-semibold text-sky-200">Testnet only.</span> This screen runs on Stellar
        testnet, separately from the rest of the app, which stays on mainnet. Switch your wallet
        (Freighter recommended) to testnet and use testnet funds. The flow is real; the yields are
        not — there are no live incentives on testnet.
      </div>

      {!configured ? (
        <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.06] p-6 text-sm text-amber-200/90">
          <p className="font-semibold">Feature not yet configured.</p>
          <p className="mt-2 text-amber-200/70">
            Set <code>REACT_APP_LEVERAGE_FACTORY_ID</code> — runbook in{" "}
            <code>soroban-contracts/leverage-vault/DEPLOY.md</code>.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Card title="Market">
            <div className="mb-3 flex items-center gap-3">
              <PairIcons a={market?.tokenA ?? ""} b={market?.tokenB ?? ""} size={26} />
              <div>
                <div className="text-base font-semibold text-white">{market?.label}</div>
                <div className="text-[11px] text-white/45">
                  {market?.tokenA}/{market?.tokenB} LP collateral · borrow {market?.borrowSymbol} · Blend{" "}
                  {market?.blendVersion}
                </div>
              </div>
            </div>
            <dl className="space-y-2 text-sm">
              <Row k="Status" v={cfg ? (cfg.paused ? "Paused (new opens)" : "Live") : "—"} />
              <Row k="Leverage cap" v={capX ? `${capX.toFixed(2)}×` : "—"} />
              <Row k="Min health factor" v={minHf ? minHf.toFixed(2) : "—"} />
              <Row k="Keeper deleverages below" v={triggerHf ? triggerHf.toFixed(2) : "—"} />
              <Row
                k="Collateral / liability factor"
                v={factors ? `${factors.cFactor.toFixed(2)} / ${factors.lFactor.toFixed(2)}` : "—"}
              />
              <Row
                k="Liquidation boundary"
                v={factors ? `${(1 / (1 - factors.cFactor * factors.lFactor)).toFixed(2)}×` : "—"}
              />
              <Row k="Position contracts deployed" v={String(positionCount)} />
              <Row k="Blend pool" v={short(market?.blendPool)} />
              <Row k="Factory" v={short(market?.factory)} />
            </dl>
          </Card>

          <Card title="Your position">
            {!wallet ? (
              <p className="text-sm text-white/40">Connect your wallet to view your position.</p>
            ) : positionLookup === "failed" ? (
              <p className="text-sm text-amber-300/80">
                Couldn't read your position from the network. Try refreshing.
              </p>
            ) : !positionAddr ? (
              <p className="text-sm text-white/50">
                No position yet. Your first open deploys your own position contract (one extra
                signature), then opens the leverage.
              </p>
            ) : (
              <>
                <dl className="space-y-2 text-sm">
                  <Row k="Position contract" v={short(positionAddr)} />
                  <Row k="Collateral (LP)" v={health ? health.collateralLp.toFixed(4) : "—"} />
                  <Row k={`Debt (${market?.borrowSymbol})`} v={health ? health.debt.toFixed(4) : "—"} />
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-white/45">Health factor</dt>
                    <dd className={`font-mono ${hfTone(health?.healthFactor)}`}>
                      {health ? (health.healthFactor == null ? "∞" : health.healthFactor.toFixed(2)) : "—"}
                    </dd>
                  </div>
                  <Row
                    k="Leverage"
                    v={health ? (health.leverage == null ? "∞ (underwater)" : `${health.leverage.toFixed(2)}×`) : "—"}
                  />
                </dl>
                <div className="mt-4 flex items-center justify-between rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-xs">
                  <span className="text-white/60">
                    Keeper protection {managerEnabled == null ? "unknown" : managerEnabled ? "on" : "off"}
                  </span>
                  <button
                    onClick={onToggleManager}
                    disabled={busy || managerEnabled == null}
                    className="rounded-md border border-white/15 px-2 py-1 text-white/80 hover:bg-white/5 disabled:opacity-50"
                  >
                    {managerEnabled ? "Revoke" : "Allow"}
                  </button>
                </div>
              </>
            )}
            <button
              onClick={refresh}
              disabled={loading}
              className="mt-4 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-white/70 hover:bg-white/5 disabled:opacity-50"
            >
              {loading ? "Refreshing…" : "↺ Refresh"}
            </button>
          </Card>

          <Card title="Open / add leverage">
            <div className="space-y-3">
              <Field label="LP collateral to supply (optional)" value={collateralLp} onChange={setCollateralLp} placeholder="0.0" suffix="LP" />
              <Field label="Amount to flash-borrow & recycle" value={borrowAmount} onChange={setBorrowAmount} placeholder="0.0" suffix={market?.borrowSymbol} />
              <Field label="Slippage tolerance" value={slippagePct} onChange={setSlippagePct} placeholder="1.0" suffix="%" />
              {(quoting || preview) && (
                <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                  <p className="mb-2 text-[11px] uppercase tracking-wide text-white/40">
                    What happens when you sign — one atomic transaction
                  </p>
                  {quoting && !preview ? (
                    <p className="text-xs text-white/40">Quoting against live pool reserves…</p>
                  ) : preview ? (
                    <ol className="space-y-1.5 text-xs text-white/60">
                      <li><span className="text-white/35">1.</span> Blend flash-lends <b className="text-white/80">{preview.borrow.toFixed(4)}</b> {market?.borrowSymbol}</li>
                      <li><span className="text-white/35">2.</span> Half is swapped → <b className="text-white/80">{preview.pairFromSwap.toFixed(4)}</b> {market?.tokenA}</li>
                      <li><span className="text-white/35">3.</span> Both sides deposited → <b className="text-white/80">{preview.lpFromZap.toFixed(4)}</b> LP minted</li>
                      <li><span className="text-white/35">4.</span> <b className="text-white/80">{preview.addedCollateral.toFixed(4)}</b> LP added as collateral (your slippage floor); about{" "}
                        <b className="text-white/80">{preview.returnedLp.toFixed(4)}</b> LP minted above it returns to your wallet</li>
                      <li className="border-t border-white/10 pt-1.5 text-white/45">
                        Your position then holds <b className="text-white/70">{preview.totalLp.toFixed(2)}</b> LP against{" "}
                        <b className="text-white/70">{preview.totalDebt.toFixed(2)}</b> {market?.borrowSymbol} debt.
                      </li>
                    </ol>
                  ) : null}
                  {preview && (
                    <div className="mt-2 space-y-1">
                      <div className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 text-xs ${preview.overCap ? "bg-red-500/15 text-red-300" : "bg-[#37b06f]/10 text-[#7fdca9]"}`}>
                        <span>Resulting leverage (est.)</span>
                        <span className="font-mono font-semibold">
                          {Number.isFinite(preview.leverage) ? `${preview.leverage.toFixed(2)}×` : "∞"}
                          {capX != null && <span className="ml-1 font-normal opacity-70">/ {capX.toFixed(2)}× cap</span>}
                        </span>
                      </div>
                      {preview.hf != null && (
                        <div className={`flex items-center justify-between rounded-lg px-2.5 py-1.5 text-xs ${preview.underHf ? "bg-red-500/15 text-red-300" : "bg-[#37b06f]/10 text-[#7fdca9]"}`}>
                          <span>Resulting health factor (est.)</span>
                          <span className="font-mono font-semibold">
                            {preview.hf.toFixed(2)}
                            {minHf != null && <span className="ml-1 font-normal opacity-70">/ min {minHf.toFixed(2)}</span>}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={onOpen}
                disabled={busy || !wallet || quoting || !!preview?.overCap || !!preview?.underHf || !!cfg?.paused}
                className="w-full rounded-xl bg-[#37b06f] py-3 font-semibold text-black hover:brightness-110 disabled:opacity-50"
              >
                {busy
                  ? "Working…"
                  : cfg?.paused
                  ? "Market paused"
                  : quoting
                  ? "Quoting…"
                  : preview?.overCap
                  ? "Above leverage cap"
                  : preview?.underHf
                  ? "Health factor too low"
                  : positionAddr
                  ? "Open leveraged position"
                  : "Create position & open"}
              </button>
              <p className="text-[11px] leading-relaxed text-white/35">
                Estimates use live pool reserves; the contract re-checks leverage and health on-chain
                with Blend's own formula and reverts if either limit would be broken.
              </p>
            </div>
          </Card>

          <div className="space-y-5">
            <Card title="Deleverage (no wallet funds needed)">
              <div className="space-y-3">
                <Field label="LP collateral to sell and repay with" value={delevLp} onChange={setDelevLp} placeholder="0.0" suffix="LP" />
                {unzap != null && (
                  <p className="text-xs text-white/50">
                    Repays about <b className="text-white/80">{unzap.toFixed(4)}</b> {market?.borrowSymbol}; any excess goes to your wallet.
                  </p>
                )}
                <button
                  onClick={onDeleverage}
                  disabled={busy || !positionAddr}
                  className="w-full rounded-xl border border-[#37b06f]/40 py-3 font-semibold text-[#7fdca9] hover:bg-[#37b06f]/10 disabled:opacity-50"
                >
                  {busy ? "Working…" : "Deleverage"}
                </button>
                <p className="text-[11px] leading-relaxed text-white/35">
                  Each step must stay above Blend's 1.0 line in between, so a large unwind may take a few steps.
                </p>
              </div>
            </Card>

            <Card title="Repay & withdraw">
              <div className="space-y-3">
                <Field label="Repay debt (from your wallet)" value={repayAmount} onChange={setRepayAmount} placeholder="0.0" suffix={market?.borrowSymbol} />
                <Field label="Withdraw collateral (to your wallet)" value={withdrawLp} onChange={setWithdrawLp} placeholder="0.0" suffix="LP" />
                <button
                  onClick={onRepayWithdraw}
                  disabled={busy || !positionAddr}
                  className="w-full rounded-xl border border-white/15 py-3 font-semibold text-white hover:bg-white/5 disabled:opacity-50"
                >
                  {busy ? "Working…" : "Repay & withdraw"}
                </button>
                <p className="text-[11px] leading-relaxed text-white/35">
                  Over-repayment is refunded. While debt remains, the result must meet the minimum
                  health factor or be safer than before.
                </p>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-white/45">{k}</dt>
      <dd className="font-mono text-white/90">{v}</dd>
    </div>
  );
}
